import React, { useState } from "react";
import { observer } from "mobx-react";
import {
  Stack,
  Text,
  HStack,
  Box,
  Image,
  Button,
  ButtonGroup,
  Card,
  Table,
  Icon,
  IconButton,
  Flex,
} from "@chakra-ui/react";
import { DistanceUnit } from "@keita/lanepatrol-sdk";

import { useStores } from "../../shared/stores/RootStore";
import { HlaScoreView } from "./HlaScoreView";
import { HlaLane } from "../models/HlaLane";
import { SimulationLane } from "../models/SimulationLane";
import { HlaWay } from "../models/HlaWay";
import { SimulationWay } from "../models/SimulationWay";
import { CrossSectionView } from "./HlaWayDetail/cross-section/CrossSectionView";
import { StreetViewComponent } from "./StreetView/StreetViewComponent";
import {
  AttributeChange,
  FACING_LABEL,
  Lane,
  LANE_FIELDS,
  LaneFacing,
  WAY_FIELDS,
  WayLike,
} from "../models/ComparisonFields";
import {
  isEqual,
  formatNumber,
  formatChangeValue,
  getSpeedUnit,
} from "../../survey/utils/simulation-comparison";
import { ArrowBackIcon } from "../../shared/views/icons";

export function diffFields<
  Before extends object,
  After extends object,
  Key extends keyof Before & keyof After,
>(
  before: Before | null | undefined,
  after: After | null | undefined,
  fields: { key: Key; label: string }[],
): AttributeChange[] {
  if (!before || !after) return [];

  return fields
    .filter(({ key }) => !isEqual(before[key], after[key]))
    .map(({ key, label }) => ({
      key: String(key),
      label,
      hlaValue: before[key],
      simulationValue: after[key],
    }));
}

const getWayChanges = (
  hlaWay?: HlaWay,
  simulationWay?: SimulationWay,
): AttributeChange[] => diffFields(hlaWay, simulationWay, WAY_FIELDS);

const getLaneChanges = (
  hlaLane?: HlaLane | null,
  simulationLane?: SimulationLane | null,
): AttributeChange[] => diffFields(hlaLane, simulationLane, LANE_FIELDS);

const PhotoBox = ({ children }: { children: React.ReactNode }) => (
  <Box
    w="100%"
    h={{base:"310px", "2xl": "370px" }}
    aspectRatio={1}
    borderWidth="1px"
    borderRadius="md"
    overflow="hidden"
  >
    {children}
  </Box>
);

const ComparisonPhoto = ({
  photoUrl,
  alt,
  streetViewMapSource,
}: {
  photoUrl?: string | null;
  alt: string;
  streetViewMapSource?: React.ComponentProps<
    typeof StreetViewComponent
  >["streetViewMapSource"];
}) => (
  <PhotoBox>
    {photoUrl ? (
      <Image src={photoUrl} alt={alt} h="100%" w="100%" objectFit="cover" />
    ) : (
      <StreetViewComponent streetViewMapSource={streetViewMapSource} />
    )}
  </PhotoBox>
);

const ComparisonCard = ({
  title,
  photo,
  children,
}: {
  title: string;
  photo: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Card.Root flexGrow={1}>
    <Stack gap={3} p={6}>
      <Text fontSize="xl" fontWeight="bold" textAlign="center">
        {title}
      </Text>

      <Stack gap={6} align="center" flexDir={{ "2xl": "row", xl: "column" }}>
        <Box w="full">{photo}</Box>
        <Box flex={2}>{children}</Box>
      </Stack>
    </Stack>
  </Card.Root>
);

const WayStats = ({
  averageRoadSpeed,
  averageDailyTraffic,
  type,
  speedUnit,
}: Pick<WayLike, "averageRoadSpeed" | "averageDailyTraffic" | "type"> & {
  speedUnit: string;
}) => {
  const { surveyStore } = useStores();

  const roadSpeed =
    averageRoadSpeed ??
    (type ? surveyStore.getDefaultAverageRoadSpeed(type) : null);

  const aadt =
    averageDailyTraffic ??
    (type ? surveyStore.getDefaultAverageDailyTraffic(type) : null);

  return (
    <HStack
      gap={{ "2xl": 1, xl: 2 }}
      p={2}
      minW={{ "2xl": "120px", xl: "234px" }}
      fontSize="sm"
    >
      <Text color="gray.500">Speed</Text>
      <Text
        fontSize="sm"
        fontWeight="semibold"
      >{`${roadSpeed} ${speedUnit}`}</Text>
      •<Text color="gray.500">AADT</Text>
      <Text fontWeight="semibold">{formatNumber(aadt!)}</Text>
    </HStack>
  );
};

type LaneOption = {
  facing: LaneFacing;
  way: WayLike;
  lane: Lane;
};

const FacingToggle = ({
  facings,
  selected,
  onSelect,
}: {
  facings: LaneFacing[];
  selected: LaneFacing;
  onSelect: (facing: LaneFacing) => void;
}) => (
  <ButtonGroup size="xs" attached variant="outline">
    {facings.map((facing) => (
      <Button
        key={facing}
        colorPalette={facing === selected ? "primary" : "gray"}
        variant={facing === selected ? "solid" : "outline"}
        onClick={() => onSelect(facing)}
      >
        {facing === "front" ? "Front" : "Back"}
      </Button>
    ))}
  </ButtonGroup>
);

const LaneComparisonColumn = ({
  options,
  selectedFacing,
  toggleFacings,
  onSelectFacing,
  speedUnit,
  editLabel,
  onEdit,
}: {
  options: LaneOption[];
  selectedFacing: LaneFacing;
  toggleFacings: LaneFacing[];
  onSelectFacing: (facing: LaneFacing) => void;
  speedUnit: string;
  editLabel: string;
  onEdit: () => void;
}) => {
  const current = options.find((option) => option.facing === selectedFacing);

  return (
    <Stack h="100%" w="100%" minH="300px" gap={4}>
      {toggleFacings.length > 1 && (
        <HStack justify="center">
          <FacingToggle
            facings={toggleFacings}
            selected={selectedFacing}
            onSelect={onSelectFacing}
          />
        </HStack>
      )}

      {current ? (
        <Stack align="center" gap={4} minW={0}>
          <Box minW={0} overflow="hidden" w="100%">
            <CrossSectionView
              lit={!!current.way.lit}
              lane={current.lane}
              canEdit={false}
              readOnly
              isSelected={false}
            />
          </Box>

          <WayStats
            averageRoadSpeed={current.way.averageRoadSpeed}
            averageDailyTraffic={current.way.averageDailyTraffic}
            type={current.way.type}
            speedUnit={speedUnit}
          />
        </Stack>
      ) : (
        <Box
          minH="180px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderWidth="1px"
          borderStyle="dashed"
          borderRadius="md"
          bg="gray.50"
        >
          <Text fontSize="sm" color="gray.500">
            No {FACING_LABEL[selectedFacing].toLowerCase()} available
          </Text>
        </Box>
      )}

      <Box>
        <HlaScoreView score={current?.lane.score!!} />
      </Box>

      <Button
        variant="outline"
        colorPalette="primary"
        size="sm"
        fontSize="sm"
        alignSelf="center"
        onClick={onEdit}
      >
        {editLabel}
      </Button>
    </Stack>
  );
};

const ChangeCard = ({
  change,
  speedUnit,
}: {
  change: AttributeChange;
  speedUnit: string;
}) => (
  <Table.Row>
    <Table.Cell>
      <Text fontWeight="semibold">{change.label}</Text>
    </Table.Cell>

    <Table.Cell>
      <Text whiteSpace="pre-line">
        {formatChangeValue(
          change.key,
          change.hlaValue,
          change.simulationValue,
          speedUnit,
        )}
      </Text>
    </Table.Cell>

    <Table.Cell>
      <Text whiteSpace="pre-line">
        {formatChangeValue(
          change.key,
          change.simulationValue,
          change.hlaValue,
          speedUnit,
        )}
      </Text>
    </Table.Cell>
  </Table.Row>
);

const WhatChanged = ({
  changes,
  speedUnit,
  facingLabel,
}: {
  changes: AttributeChange[];
  speedUnit: string;
  facingLabel?: string;
}) => (
  <Card.Root alignSelf={"center"}>
    <Stack bg="white" py={4} gap={2} align={"center"}>
      <Stack gap={0} align="center">
        <Text fontWeight="bold" flexShrink={0}>
          What changed
        </Text>
        {facingLabel && (
          <Text fontSize="xs" color="gray.500">
            {facingLabel}
          </Text>
        )}
      </Stack>

      {changes.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          No attribute changes detected.
        </Text>
      ) : (
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Attribute</Table.ColumnHeader>
              <Table.ColumnHeader>Before</Table.ColumnHeader>
              <Table.ColumnHeader>After</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {changes.map((change) => (
              <ChangeCard
                key={change.key}
                change={change}
                speedUnit={speedUnit}
              />
            ))}
          </Table.Body>
        </Table.Root>
      )}
    </Stack>
  </Card.Root>
);

const HlaSimulationComparisonViewBase = ({
  distanceUnit,
}: {
  distanceUnit: DistanceUnit;
}) => {
  const { hlaStore, simulationStore, mapStore } = useStores();

  const speedUnit = getSpeedUnit(distanceUnit);

  const hlaWay = hlaStore.getSelected();
  const simulationWay = simulationStore.selectedSimulationWay;

  const hlaFrontFacingLane = hlaWay?.frontFacingLane;
  const hlaBackFacingLane = hlaWay?.backFacingLane;
  const simulationFrontFacingLane = simulationWay?.frontFacingLane;
  const simulationBackFacingLane = simulationWay?.backFacingLane;

  const hlaOptions: (LaneOption | null)[] = [
    hlaWay && hlaFrontFacingLane
      ? { facing: "front" as const, way: hlaWay, lane: hlaFrontFacingLane }
      : null,
    hlaWay && hlaBackFacingLane
      ? { facing: "back" as const, way: hlaWay, lane: hlaBackFacingLane }
      : null,
  ];
  const resolvedHlaOptions: LaneOption[] = hlaOptions.filter(
    (option): option is LaneOption => Boolean(option),
  );

  const simulationOptions: (LaneOption | null)[] = [
    simulationWay && simulationFrontFacingLane
      ? {
          facing: "front" as const,
          way: simulationWay,
          lane: simulationFrontFacingLane,
        }
      : null,
    simulationWay && simulationBackFacingLane
      ? {
          facing: "back" as const,
          way: simulationWay,
          lane: simulationBackFacingLane,
        }
      : null,
  ];
  const resolvedSimulationOptions: LaneOption[] = simulationOptions.filter(
    (option): option is LaneOption => Boolean(option),
  );

  const availableFacings: LaneFacing[] = (["front", "back"] as const).filter(
    (facing) =>
      resolvedHlaOptions.some((option) => option.facing === facing) ||
      resolvedSimulationOptions.some((option) => option.facing === facing),
  );

  const [selectedFacing, setSelectedFacing] = useState<LaneFacing>("front");

  const effectiveFacing = availableFacings.includes(selectedFacing)
    ? selectedFacing
    : availableFacings[0] ?? selectedFacing;

  const wayChanges = getWayChanges(hlaWay, simulationWay);

  const frontFacingLaneChanges = getLaneChanges(
    hlaFrontFacingLane,
    simulationFrontFacingLane,
  );

  const backFacingLaneChanges = getLaneChanges(
    hlaBackFacingLane,
    simulationBackFacingLane,
  );

  const selectedLaneChanges =
    effectiveFacing === "front"
      ? frontFacingLaneChanges
      : backFacingLaneChanges;

  const changes = [...wayChanges, ...selectedLaneChanges];

  const afterPhotoUrl = simulationWay?.photo?.url;

  return (
    <Stack gap={{ "2xl": 6, xl: 3 }} py={4} px={6} w="full">
      <Box flexShrink={0} width="40px">
        <IconButton
          size="sm"
          variant="ghost"
          onClick={() => {
            hlaStore.clearSelected();
          }}
        >
          <Icon as={ArrowBackIcon} />
        </IconButton>
      </Box>
      <Text fontSize="xl" fontWeight="bold" flexShrink={0}>
        Comparison view
      </Text>

      <Flex flexDir={{base:"column", "xl":"row"}} gap={{ "2xl": 6, xl: 3 }} alignItems={"stretch"}>
        <ComparisonCard
          title="Before"
          photo={
            <ComparisonPhoto
              photoUrl={null}
              alt="HLA reference"
              streetViewMapSource={mapStore.streetViewMapSource}
            />
          }
        >
          <LaneComparisonColumn
            options={resolvedHlaOptions}
            selectedFacing={effectiveFacing}
            toggleFacings={availableFacings}
            onSelectFacing={setSelectedFacing}
            speedUnit={speedUnit}
            editLabel="Edit HLA"
            onEdit={() => hlaStore.editHla()}
          />
        </ComparisonCard>

        <ComparisonCard
          title="After"
          photo={
            <ComparisonPhoto
              photoUrl={afterPhotoUrl}
              alt="Simulation reference"
              streetViewMapSource={mapStore.streetViewMapSource}
            />
          }
        >
          <LaneComparisonColumn
            options={resolvedSimulationOptions}
            selectedFacing={effectiveFacing}
            toggleFacings={availableFacings}
            onSelectFacing={setSelectedFacing}
            speedUnit={speedUnit}
            editLabel="Edit simulation"
            onEdit={() => simulationStore.editSimulation()}
          />
        </ComparisonCard>
      </Flex>

      <WhatChanged
        changes={changes}
        speedUnit={speedUnit}
        facingLabel={
          availableFacings.length > 1
            ? FACING_LABEL[effectiveFacing]
            : undefined
        }
      />
    </Stack>
  );
};

export const HlaSimulationComparisonView = observer(
  HlaSimulationComparisonViewBase,
);
